import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, MapPin, Search, Ticket, 
  X, ArrowRight, Navigation, Locate, Loader2
} from 'lucide-react';
import { getTourDates, getShows, searchByPostcode, searchByLocation } from '../services/cmsData';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

// Helper to get correct image path
const getImagePath = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${PUBLIC_URL}${path}`;
};

// Helper to group dates by venue and date (combining multiple times)
const groupDatesByVenueAndDay = (dates) => {
  const grouped = {};
  dates.forEach(date => {
    const key = `${date.showId}-${date.venue}-${date.date}`;
    if (!grouped[key]) {
      grouped[key] = {
        ...date,
        times: [{ time: date.time, ticketUrl: date.ticketUrl, status: date.status, id: date.id }]
      };
    } else {
      grouped[key].times.push({ time: date.time, ticketUrl: date.ticketUrl, status: date.status, id: date.id });
    }
  });
  // Sort times within each group
  Object.values(grouped).forEach(g => {
    g.times.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  });
  return Object.values(grouped);
};

export default function TourDatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tourDates, setTourDates] = useState([]);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [searchedPostcode, setSearchedPostcode] = useState('');
  const [selectedShow, setSelectedShow] = useState(searchParams.get('showId') || 'all');
  const [viewMode, setViewMode] = useState('list');

  // Initial load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [datesData, showsData] = await Promise.all([
          getTourDates(),
          getShows()
        ]);
        setTourDates(datesData);
        setShows(showsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Search with postcode
  const handleSearch = async () => {
    if (!postcodeSearch.trim()) {
      setSearching(true);
      try {
        const data = await getTourDates({ showId: selectedShow });
        setTourDates(data);
        setSearchedPostcode('');
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setSearching(false);
      }
      return;
    }

    setSearching(true);
    try {
      // Use external API for postcode search (geocoding)
      const data = await searchByPostcode(postcodeSearch.trim(), selectedShow);
      setTourDates(data);
      setSearchedPostcode(postcodeSearch.trim());
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setSearching(false);
    }
  };

  // Use browser geolocation
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    setPostcodeSearch('');
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        console.log('Got location:', latitude, longitude);
        
        try {
          const data = await searchByLocation(latitude, longitude, selectedShow);
          setTourDates(data);
          setSearchedPostcode('My Location');
        } catch (error) {
          console.error('Error searching by location:', error);
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setGettingLocation(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            alert('Please allow location access to use this feature');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information unavailable');
            break;
          case error.TIMEOUT:
            alert('Location request timed out');
            break;
          default:
            alert('Unable to get your location');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Filter by show
  const filteredDates = tourDates.filter((date) => {
    return selectedShow === 'all' || date.showId === selectedShow;
  });

  // Group dates by venue and day first (combine multiple times)
  const consolidatedDates = groupDatesByVenueAndDay(filteredDates);

  // Group by distance ranges if searching, otherwise by month
  const groupedDates = searchedPostcode 
    ? consolidatedDates.reduce((groups, date) => {
        let groupName;
        if (date.distance === null) {
          groupName = 'Other Locations';
        } else if (date.distance <= 10) {
          groupName = 'Within 10 miles';
        } else if (date.distance <= 25) {
          groupName = 'Within 25 miles';
        } else if (date.distance <= 50) {
          groupName = 'Within 50 miles';
        } else {
          groupName = 'Further Away';
        }
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push(date);
        return groups;
      }, {})
    : consolidatedDates.reduce((groups, date) => {
        const monthYear = new Date(date.date).toLocaleDateString('en-GB', {
          month: 'long',
          year: 'numeric'
        });
        if (!groups[monthYear]) groups[monthYear] = [];
        groups[monthYear].push(date);
        return groups;
      }, {});

  const handleShowFilter = async (value) => {
    setSelectedShow(value);
    if (value === 'all') {
      searchParams.delete('showId');
    } else {
      searchParams.set('showId', value);
    }
    setSearchParams(searchParams);

    if (searchedPostcode) {
      setSearching(true);
      try {
        // Use searchByPostcode from cmsData service
        const data = await searchByPostcode(searchedPostcode, value);
        setTourDates(data);
      } catch (error) {
        console.error('Error filtering:', error);
      } finally {
        setSearching(false);
      }
    }
  };

  const clearFilters = async () => {
    setSelectedShow('all');
    setPostcodeSearch('');
    setSearchedPostcode('');
    setSearchParams({});
    
    setSearching(true);
    try {
      // Use getTourDates from cmsData service
      const data = await getTourDates();
      setTourDates(data);
    } catch (error) {
      console.error('Error resetting:', error);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="tour-dates-page" className="min-h-screen bg-gray-50">
      {/* Search Header Section */}
      <section className="bg-brand-primary pt-32 pb-8 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
              Find Tour Dates
            </h1>
            <p className="font-body text-white/80 text-lg">
              Search by postcode to find shows near you
            </p>
          </motion.div>

          {/* Search Controls */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-md rounded-2xl p-4 lg:p-6 border border-white/20"
          >
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Postcode Search */}
              <div className="flex-1 w-full lg:max-w-md">
                <div className="relative">
                  <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Enter your postcode or town..."
                    value={postcodeSearch}
                    onChange={(e) => setPostcodeSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="postcode-search"
                    className="w-full pl-12 pr-4 h-14 bg-white border-0 text-gray-900 placeholder:text-gray-400 rounded-xl text-base font-medium focus:ring-2 focus:ring-accent-gold/50 transition-all shadow-sm"
                  />
                </div>
              </div>

              {/* Show Filter */}
              <select
                value={selectedShow}
                onChange={(e) => handleShowFilter(e.target.value)}
                data-testid="show-filter"
                className="h-14 px-5 bg-white border-0 text-gray-900 rounded-xl text-base font-medium focus:ring-2 focus:ring-accent-gold/50 w-full lg:w-56 shadow-sm cursor-pointer"
              >
                <option value="all">All Shows</option>
                {shows.map((show) => (
                  <option key={show.id} value={show.id}>
                    {show.shortTitle}
                  </option>
                ))}
              </select>

              {/* Use My Location Button */}
              <button
                onClick={handleUseMyLocation}
                disabled={gettingLocation || searching}
                data-testid="location-btn"
                className="h-14 px-4 text-base font-bold rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-70 w-full lg:w-auto shadow-sm bg-white/20 text-white border border-white/30 hover:bg-white/30"
                title="Use my location"
              >
                {gettingLocation ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Locate className="w-5 h-5" />
                )}
                <span className="lg:hidden">Use My Location</span>
              </button>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                disabled={searching || gettingLocation}
                data-testid="search-btn"
                className="h-14 px-8 text-base font-bold rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-70 w-full lg:w-auto shadow-sm"
                style={{ backgroundColor: '#D4AF37', color: '#020120' }}
              >
                <Search className={`w-5 h-5 ${searching ? 'animate-spin' : ''}`} />
                Search
              </button>
            </div>

            {/* Results Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center gap-4">
                <span className="text-white/80 text-sm">
                  <span className="text-accent-gold font-bold text-lg">{filteredDates.length}</span> dates found
                </span>
                
                {(selectedShow !== 'all' || searchedPostcode) && (
                  <button
                    onClick={clearFilters}
                    className="text-white/70 hover:text-white text-sm flex items-center gap-1 bg-white/10 px-3 py-1.5 rounded-full transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Clear filters
                  </button>
                )}
              </div>
              
              {/* View Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-white/60 text-sm mr-2">View:</span>
                <div className="flex items-center bg-white/10 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('list')}
                    data-testid="list-view-btn"
                    className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-brand-primary shadow-sm' : 'text-white/70 hover:text-white'}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    data-testid="grid-view-btn"
                    className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white text-brand-primary shadow-sm' : 'text-white/70 hover:text-white'}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tour Dates - List or Grid View */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={`bg-gray-100 rounded-2xl animate-pulse ${viewMode === 'grid' ? 'h-72' : 'h-28'}`} />
              ))}
            </div>
          ) : filteredDates.length > 0 ? (
            <div className="space-y-12">
              {Object.entries(groupedDates).map(([groupName, dates]) => (
                <div key={groupName}>
                  <h2 className="font-display text-2xl font-bold text-brand-dark mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-primary/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-brand-primary" />
                    </div>
                    {groupName}
                    <span className="text-sm font-normal text-gray-500">({dates.length})</span>
                  </h2>
                  
                  {/* Grid View */}
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                      {dates.map((date, index) => (
                        <motion.div
                          key={date.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          data-testid={`tour-date-item-${date.id}`}
                          className="group bg-gray-50 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-transparent hover:border-gray-200"
                        >
                          {/* Poster Header */}
                          <div className="relative h-32 overflow-hidden">
                            <img
                              src={getImagePath(date.showPoster)}
                              alt={date.showTitle}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                            <div className="absolute bottom-3 left-3 right-3">
                              <p className="text-white font-bold text-sm truncate">{date.showShortTitle}</p>
                            </div>
                            {date.distance !== null && date.distance !== undefined && (
                              <div className="absolute top-3 right-3 bg-white/90 text-brand-primary px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                <Navigation className="w-3 h-3" />
                                {date.distance}mi
                              </div>
                            )}
                          </div>
                          
                          {/* Card Content */}
                          <div className="p-4">
                            <p className="font-display font-bold text-lg text-brand-dark mb-1">{date.town}</p>
                            <p className="text-gray-600 text-sm mb-3 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {date.venue}
                            </p>
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <p className="font-bold text-brand-primary text-sm">
                                  {new Date(date.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                </p>
                                <p className="text-gray-500 text-xs">
                                  {date.times ? date.times.map(t => t.time).join(' & ') : date.time}
                                </p>
                              </div>
                            </div>
                            {/* Ticket buttons for each time */}
                            <div className="flex flex-wrap gap-2">
                              {date.times ? (
                                date.times.map((t) => (
                                  <a
                                    key={t.id}
                                    href={t.ticketUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-accent-gold text-brand-dark px-3 py-1.5 rounded-full text-xs font-bold hover:bg-brand-primary hover:text-white transition-all"
                                  >
                                    {t.time}
                                  </a>
                                ))
                              ) : (
                                <a
                                  href={date.ticketUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bg-accent-gold text-brand-dark px-4 py-2 rounded-full text-xs font-bold hover:bg-brand-primary hover:text-white transition-all"
                                >
                                  Tickets
                                </a>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    /* List View */
                    <div className="space-y-4">
                      {dates.map((date, index) => (
                        <motion.div
                          key={date.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          data-testid={`tour-date-item-${date.id}`}
                          className="group bg-gray-50 rounded-xl p-5 hover:bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 border border-transparent hover:border-gray-200"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                            {/* Show Info */}
                            <div className="flex items-center gap-4 lg:w-80">
                              <img
                                src={getImagePath(date.showPoster)}
                                alt={date.showTitle}
                                className="w-16 h-22 object-cover shadow group-hover:scale-105 transition-transform"
                              />
                              <div>
                                <Link
                                  to={`/shows/${date.showId}`}
                                  className="font-display text-lg font-bold text-brand-dark hover:text-brand-primary transition-colors"
                                >
                                  {date.showShortTitle}
                                </Link>
                                {date.distance !== null && date.distance !== undefined && (
                                  <div className="mt-1 inline-flex items-center gap-1 bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded-full text-xs font-semibold">
                                    <Navigation className="w-3 h-3" />
                                    {date.distance} mi
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Location & Date */}
                            <div className="flex-1 grid sm:grid-cols-2 gap-4">
                              <div>
                                <p className="font-display font-bold text-lg text-brand-dark">{date.town}</p>
                                <p className="text-gray-600 text-sm flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {date.venue}
                                </p>
                              </div>
                              <div>
                                <p className="font-bold text-brand-primary">
                                  {new Date(date.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                                </p>
                                <p className="text-gray-600 text-sm">
                                  {date.times ? date.times.map(t => t.time).join(' & ') : date.time}
                                </p>
                              </div>
                            </div>

                            {/* CTA - Show multiple ticket buttons if multiple times */}
                            <div className="flex items-center gap-2 lg:w-80 lg:flex-shrink-0 justify-end flex-wrap">
                              {date.times ? (
                                date.times.map((t, idx) => (
                                  <a
                                    key={t.id}
                                    href={t.ticketUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 bg-accent-gold text-brand-dark px-4 py-2 rounded-full text-sm font-bold hover:bg-brand-primary hover:text-white transition-all"
                                  >
                                    <Ticket className="w-4 h-4" />
                                    {t.time}
                                  </a>
                                ))
                              ) : (
                                <a
                                  href={date.ticketUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 bg-accent-gold text-brand-dark px-5 py-2 rounded-full text-sm font-bold hover:bg-brand-primary hover:text-white transition-all"
                                >
                                  <Ticket className="w-4 h-4" />
                                  Tickets
                                </a>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="font-display text-2xl font-bold text-brand-dark mb-3">
                No Tour Dates Found
              </h3>
              <p className="font-body text-gray-600 mb-8">
                {postcodeSearch || selectedShow !== 'all'
                  ? 'Try adjusting your filters or search terms'
                  : 'Check back soon for upcoming tour dates'}
              </p>
              {(postcodeSearch || selectedShow !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="px-8 py-4 bg-brand-primary text-white rounded-full font-semibold hover:bg-brand-dark transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-dark">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">
            Not Sure Which Show to See?
          </h2>
          <p className="font-body text-white/80 text-lg mb-8">
            Browse all our productions and find the perfect family day out
          </p>
          <Link
            to="/shows"
            className="inline-flex items-center gap-3 bg-accent-gold text-brand-dark px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 hover:bg-white hover:scale-105"
          >
            Explore All Shows
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
